import React, { useEffect, useRef, useState } from "react";
import api from "../api/client";
import { CommandQueuedResponse } from "../types";
import { MIN_LENGTH, checkPassword } from "../utils/passwordPolicy";
import { backdropClose } from "./ModalBackdrop";

/* Shared "Onboard new user" flow — create the account in on-prem AD and/or
 * Microsoft 365 (Entra) in ONE submit, then reveal each one-time credential.
 * Used from both the Active Directory page and the Microsoft 365 dashboard. */

type CredResult = { label: string; upn: string; password: string };

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" {...backdropClose(onClose)}>
      <div
        className="bg-white dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

export default function OnboardUserModal({
  customerId,
  onClose,
  onRefresh,
}: {
  customerId: number;
  onClose: () => void;
  onRefresh: () => void;
}) {
  // Generated is the default and stays the recommendation. Typed exists for the
  // administrator stood next to the new starter who has to read it out — that
  // person was going to the admin centre instead, which puts the change outside
  // the audit trail entirely.
  const [pwMode, setPwMode] = useState<"generate" | "type">("generate");
  const [typedPw, setTypedPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [doAd, setDoAd] = useState(true);
  const [doM365, setDoM365] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState<CredResult[] | null>(null);
  const [name, setName] = useState("");
  const [given, setGiven] = useState("");
  const [surname, setSurname] = useState("");
  const [upn, setUpn] = useState("");
  const [email, setEmail] = useState("");
  const [sam, setSam] = useState("");
  const [skuId, setSkuId] = useState("");
  const [skus, setSkus] = useState<{ id: string; label: string }[]>([]);
  /* WHERE the AD account lands.
   *
   * Reported: "it placed it under the 'Users' OU... maybe I can have it with
   * a dropdown to change the ou". CN=Users is what AD does when nobody says
   * otherwise, and moving the account afterwards is a second job that also
   * delays the M365 sync - which is what made a freshly-created user seem to
   * vanish. Empty value = let AD use its default, so the behaviour for
   * anyone who ignores this is exactly what it was. */
  const [ouDn, setOuDn] = useState("");
  const [ous, setOus] = useState<{ name: string; dn: string }[]>([]);
  const [ouState, setOuState] = useState<"idle" | "loading" | "failed">("idle");
  // Kept OUT of state so that starting the fetch cannot re-trigger the effect.
  const ouStartedRef = useRef(false);

  useEffect(() => {
    if (!doAd || ouStartedRef.current) return;
    // A REF, not ouState, guards the "already started" case.
    //
    // The first version had ouState in the dependency array. Setting it to
    // "loading" inside the effect re-ran the effect, whose CLEANUP set
    // cancelled = true and killed the poll that had just been started - so
    // the dropdown sat on "Reading the domain's OUs..." for ever. The state
    // a fetch sets must never also be what re-triggers it.
    ouStartedRef.current = true;
    let cancelled = false;
    setOuState("loading");
    (async () => {
      try {
        const r = await api.post(`/api/v1/ad/${customerId}/ous`);
        const id = r.data.command_id;
        // The AD runner answers on its next poll: ~2-3s once it is awake
        // (burst polling, agent 1.0.82+), up to ~25s from cold.
        for (let i = 0; i < 30 && !cancelled; i++) {
          await new Promise((res) => setTimeout(res, 2000));
          const p = await api.get(`/api/v1/ad/commands/${id}`);
          if (p.data.status === "completed") {
            if (!cancelled) {
              setOus(p.data.result?.ous || []);
              setOuState("idle");
            }
            return;
          }
          if (p.data.status === "failed" || p.data.status === "expired") break;
        }
        if (!cancelled) {
          setOuState("failed");
          ouStartedRef.current = false; // let a reopen try again
        }
      } catch {
        if (!cancelled) {
          setOuState("failed");
          ouStartedRef.current = false;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doAd, customerId]);

  useEffect(() => {
    if (!doM365) return;
    api
      .get(`/api/v1/m365/${customerId}/license-report`)
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : r.data?.skus || [];
        setSkus(
          rows.map((s: any) => {
            const id = s.sku_id || s.skuId || s.id || "";
            const part = s.sku_part_number || s.skuPartNumber || id;
            const avail = s.available ?? s.available_units;
            return { id, label: avail != null ? `${part} (${avail} free)` : part };
          }),
        );
      })
      .catch(() => setSkus([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doM365]);

  const submit = async () => {
    setError("");
    if (!doAd && !doM365) {
      setError("Pick at least one directory to create the account in.");
      return;
    }
    if (!name.trim()) {
      setError("Full name is required.");
      return;
    }
    if (doAd && !sam.trim()) {
      setError("Username (sAMAccountName) is required for Active Directory.");
      return;
    }
    if (doM365 && !upn.includes("@")) {
      setError("A full UPN (user@domain) is required for Microsoft 365.");
      return;
    }
    if (pwMode === "type") {
      // The server re-checks — this is a courtesy so the reason is specific
      // and arrives before the round trip.
      const bad = checkPassword(typedPw, { username: sam.trim() || upn.trim(), displayName: name.trim() });
      if (bad) {
        setError(bad);
        return;
      }
    }
    setBusy(true);
    const creds: CredResult[] = [];
    try {
      if (doAd) {
        const r = await api.post<CommandQueuedResponse>("/api/v1/ad/users", {
          customer_id: customerId,
          name: name.trim(),
          sam_account_name: sam.trim(),
          user_principal_name: upn.trim() || null,
          email: email.trim() || null,
          given_name: given.trim() || null,
          surname: surname.trim() || null,
          ou_dn: ouDn || null,
          // Omitted entirely when generating, so the server keeps its own
          // default rather than being handed an empty string to interpret.
          ...(pwMode === "type" ? { password: typedPw } : {}),
        });
        creds.push({
          label: "Active Directory — provisions on the agent's next check-in",
          upn: upn.trim() || sam.trim(),
          password:
            pwMode === "type"
              ? typedPw
              : r.data.generated_password || "(generated on the DC)",
        });
      }
      if (doM365) {
        const r = await api.post<{ initial_password?: string; license_assigned?: boolean }>(
          `/api/v1/m365/${customerId}/users`,
          {
            user_principal_name: upn.trim(),
            display_name: name.trim(),
            sku_id: skuId || null,
            // The field above says "used for both directories". It was only
            // ever sent to AD, so onboarding to both handed the person two
            // different passwords and the one read out to them was wrong.
            ...(pwMode === "type" ? { password: typedPw } : {}),
          },
        );
        creds.push({
          label: r.data.license_assigned
            ? "Microsoft 365 — created, license assigned"
            : "Microsoft 365 — created",
          upn: upn.trim(),
          password: pwMode === "type" ? typedPw : r.data.initial_password || "(none)",
        });
      }
      setResults(creds);
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  const field = (
    label: string,
    value: string,
    setter: (v: string) => void,
    placeholder = "",
    required = false,
  ) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <input
        value={value}
        onChange={(e) => setter(e.target.value)}
        placeholder={placeholder}
        className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
      />
    </div>
  );

  if (results) {
    return (
      <Modal title="User created" onClose={onClose}>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
          Save these one-time credentials now — they won't be shown again.
        </p>
        <div className="space-y-3 mb-4">
          {results.map((c, i) => (
            <div key={i} className="border dark:border-gray-600 rounded-lg p-3">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{c.label}</div>
              <div className="text-sm mb-1">{c.upn}</div>
              <div className="bg-gray-900 text-green-300 font-mono text-sm rounded px-3 py-2 select-all break-all">
                {c.password}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={() =>
              navigator.clipboard?.writeText(
                results.map((c) => `${c.label}\n${c.upn}\n${c.password}`).join("\n\n"),
              )
            }
            className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Copy all
          </button>
          <button onClick={onClose} className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg">
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Onboard new user" onClose={onClose}>
      <div className="space-y-2 mb-4">
        <div className="text-xs font-medium text-gray-600 dark:text-gray-300">Create the account in:</div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={doAd} onChange={(e) => setDoAd(e.target.checked)} />
          On-prem Active Directory
          <span className="text-[11px] text-gray-500 dark:text-gray-400">(syncs up to Entra)</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={doM365} onChange={(e) => setDoM365(e.target.checked)} />
          Microsoft 365 / Entra
          <span className="text-[11px] text-gray-500 dark:text-gray-400">(cloud + optional license)</span>
        </label>
        {doAd && doM365 && (
          <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 rounded px-2 py-1">
            Heads-up: in a hybrid environment, creating in both directly makes two separate
            identities unless Entra Connect matches them by UPN. Usually create in AD only and let
            it sync, then assign the license.
          </p>
        )}
      </div>

      <div className="space-y-3 mb-4">
        {field("Full name", name, setName, "Jane Roe", true)}
        <div className="grid grid-cols-2 gap-2">
          {field("Given name", given, setGiven, "Jane")}
          {field("Surname", surname, setSurname, "Roe")}
        </div>
        {field("User principal name (UPN)", upn, setUpn, "jroe@customer.com", doM365)}
        {field("Email", email, setEmail, "jroe@customer.com")}
        {doAd && field("Username (sAMAccountName)", sam, setSam, "jroe", true)}
        {doAd && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Create in
            </label>
            <select
              value={ouDn}
              onChange={(e) => setOuDn(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            >
              <option value="">
                {ouState === "loading"
                  ? "Reading the domain's OUs…"
                  : "Default container (Users)"}
              </option>
              {ous.map((o) => (
                <option key={o.dn} value={o.dn}>
                  {o.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              {ouState === "failed"
                ? "Could not read the OUs from the domain — the account will go to the default container."
                : "Putting it in the right OU now avoids moving it later, which delays the Microsoft 365 sync."}
            </p>
          </div>
        )}
        {doM365 && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">License</label>
            <select
              value={skuId}
              onChange={(e) => setSkuId(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            >
              <option value="">No license (assign later)</option>
              {skus.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
              Assigning a license lets Exchange auto-provision the mailbox.
            </p>
          </div>
        )}
        {/* Password. Generated first and selected by default, because it is
            the right answer nearly always — the typed option is here so that
            "I need to read it to them now" does not send someone to the admin
            centre and out of the audit trail. */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-xs mb-2">
            <button
              type="button"
              onClick={() => setPwMode("generate")}
              className={
                pwMode === "generate"
                  ? "px-3 py-1.5 bg-blue-600 text-white"
                  : "px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              }
            >
              Generate a password
            </button>
            <button
              type="button"
              onClick={() => setPwMode("type")}
              className={
                pwMode === "type"
                  ? "px-3 py-1.5 bg-blue-600 text-white"
                  : "px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              }
            >
              Set one myself
            </button>
          </div>

          {pwMode === "generate" ? (
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              A strong password is generated for each directory and shown once after creation.
            </p>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <input
                  type={showPw ? "text" : "password"}
                  value={typedPw}
                  onChange={(e) => setTypedPw(e.target.value)}
                  placeholder={`At least ${MIN_LENGTH} characters`}
                  autoComplete="new-password"
                  className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                />
                {/* Visible by choice: somebody typing a password they are about
                    to read aloud needs to see what they typed. */}
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:underline px-1"
                >
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                Used for both directories. The account must change it at first sign-in. The
                server checks it against the same rules the directory will, so a weak one is
                refused here rather than failing on the domain controller a minute later.
              </p>
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded mb-3">{error}</p>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={busy}
          className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
        >
          {busy ? "Creating…" : "Create user"}
        </button>
      </div>
    </Modal>
  );
}
